import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import { fetchData, postData } from '../../../apiService';

const PurchaseReturnModal = ({ open, onClose, onSave, companyId, branchId, token, suppliers = [], setLoading, setLoadingMessage, setError, setSuccess, fetchPurchaseReturns, setCreateModalOpen, userId }) => {
  console.log('PurchaseReturnModal props:', { open, companyId, branchId, token: token ? 'exists' : 'missing', suppliersCount: suppliers.length });

  const [formData, setFormData] = useState({
    billNo: '',
    billDate: new Date().toISOString().split('T')[0],
    purchaseId: '',
    purchaseNo: '',
    supplierId: '',
    supplier: '',
    vatNo: '',
    invType: '',
    vatType: 'Excluding Tax',
    CurrencyTypeId: null,
    CurrencyName: ''
  });

  // Currency dropdown state
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  const [itemsList, setItemsList] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [items, setItems] = useState([]);
  const [viewAllItemsModalOpen, setViewAllItemsModalOpen] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState({
    freight: 0,
    packing: 0,
    tcsAmount: 0,
    paidAmount: 0,
    remarks: ''
  });

  const [summary, setSummary] = useState({
    subtotal: 0,
    discount: 0,
    taxable: 0,
    // cgst: 0,
    // sgst: 0,
    // igst: 0,
    vatamount: 0,
    //igst: 0,
    freightVAT: 0,
    packingVAT: 0,
    effectiveVATRate: 5,
    tcs: 0,
    roundOff: 0,
    total: 0,
    paid: 0
  });

  // VAT-only calculation (no IGST/CGST/SGST splitting)
  // This module only uses VATAmount, FreightVATAmount, and PackingVATAmount

  // Fetch currencies
  useEffect(() => {
    const fetchCurrencies = async () => {
      if (!companyId || !token || !open) return;
      setCurrencyLoading(true);
      try {
        const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
        if (Array.isArray(response)) {
          setCurrencies(response);
        } else if (response?.response_data && Array.isArray(response.response_data)) {
          setCurrencies(response.response_data);
        } else {
          setCurrencies([]);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
        setCurrencies([]);
      } finally {
        setCurrencyLoading(false);
      }
    };

    fetchCurrencies();
  }, [companyId, token, open]);

  // Dynamic currency formatting based on selected currency
  const formatCurrency = (amt) => {
    const getCurrencySymbol = (name) => {
      if (!name) return 'AED';
      const n = (typeof name === 'string' ? name : '').toLowerCase().trim();
      if (!n) return 'AED';
      const symbolMap = {
        'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
        'usd': '$', 'dollar': '$', 'us dollar': '$',
        'eur': '€', 'euro': '€',
        'gbp': '£', 'pound': '£', 'british pound': '£',
        'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
        'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
        'jpy': '¥', 'yen': '¥',
        'cny': '¥', 'yuan': '¥', 'rmb': '¥',
        'aud': 'A$', 'australian dollar': 'A$',
        'cad': 'C$', 'canadian dollar': 'C$',
        'sgd': 'S$', 'singapore dollar': 'S$',
        'chf': 'CHF', 'swiss franc': 'CHF',
        'myr': 'RM', 'ringgit': 'RM',
        'thb': '฿', 'baht': '฿',
        'krw': '₩', 'won': '₩',
        'bdt': '৳', 'taka': '৳',
        'lkr': 'Rs', 'sri lankan rupee': 'Rs',
        'pkr': '₨', 'pakistani rupee': '₨',
        'npr': 'रू', 'nepalese rupee': 'रू',
        'zar': 'R', 'rand': 'R',
        'rub': '₽', 'ruble': '₽',
        'try': '₺', 'lira': '₺',
        'idr': 'Rp', 'rupiah': 'Rp',
        'php': '₱', 'peso': '₱',
        'vnd': '₫', 'dong': '₫',
        'ngn': '₦', 'naira': '₦',
        'egp': 'E£', 'egyptian pound': 'E£',
        'qar': 'QR', 'qatari riyal': 'QR',
        'kwd': 'KD', 'kuwaiti dinar': 'KD',
        'bhd': 'BD', 'bahraini dinar': 'BD',
        'omr': 'OMR', 'omani rial': 'OMR'
      };
      if (symbolMap[n]) return symbolMap[n];
      for (const [key, symbol] of Object.entries(symbolMap)) {
        if (n.includes(key)) return symbol;
      }
      return name;
    };
    try {
      const symbol = getCurrencySymbol(formData.CurrencyName);
      return `${symbol} ${(parseFloat(amt) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch (e) {
      return ` ${(parseFloat(amt) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Fetch items list
  useEffect(() => {
    const fetchItems = async () => {
      if (!companyId || !token || !open) return;

      try {
        const response = await fetchData('ItemApi', `/${companyId}`, token);
        const itemsData = response.response_data || response || [];
        console.log('Items fetched:', itemsData);
        setItemsList(Array.isArray(itemsData) ? itemsData : []);
      } catch (error) {
        console.error('Error fetching items:', error);
        setItemsList([]);
      }
    };

    fetchItems();
  }, [companyId, token, open]);

  // Fetch purchases list
  useEffect(() => {
    const fetchPurchases = async () => {
      if (!companyId || !branchId || !token || !open) return;

      try {
        const response = await fetchData('PurchaseApi', `/${companyId}/${branchId}`, token);
        const purchasesData = response.response_data || response || [];
        console.log('Purchases fetched:', purchasesData);
        if (purchasesData.length > 0) {
          console.log('First purchase object keys:', Object.keys(purchasesData[0]));
          console.log('First purchase sample:', JSON.stringify(purchasesData[0], null, 2));
        }
        setPurchases(Array.isArray(purchasesData) ? purchasesData : []);
      } catch (error) {
        console.error('Error fetching purchases:', error);
        setPurchases([]);
      }
    };

    fetchPurchases();
  }, [companyId, branchId, token, open]);

  // Fetch MaxBillNo
  useEffect(() => {
    const fetchMaxBillNo = async () => {
      if (!companyId || !branchId || !token || !open) return;

      try {
        const response = await fetchData('PurchaseReturnApi', `/MaxBillno/${companyId}/${branchId}`, token);
        const data = response.response_data || response || {};
        const billNumber = data.BillNumber || data.billNumber || 1;
        const billPrefix = data.BillPrefix || data.billPrefix || 'DN';
        const finYear = data.FinYear || data.finYear || '';

        // Format: prefix/billno/finyear
        const fullBillNo = `${billPrefix}/${billNumber}/${finYear}`;
        setFormData(prev => ({ ...prev, billNo: fullBillNo }));
      } catch (error) {
        console.error('Error fetching MaxBillNo:', error);
        setFormData(prev => ({ ...prev, billNo: '1' }));
      }
    };

    fetchMaxBillNo();
  }, [companyId, branchId, token, open]);

  // Handle supplier selection - auto-fill InvoiceTypeName and GSTNumber
  const handleSupplierChange = (supplierId) => {
    console.log('Supplier selected:', supplierId);

    if (!supplierId) {
      setFormData(prev => ({
        ...prev,
        supplierId: '',
        supplier: '',
        vatNo: '',
        invType: '',
        CurrencyTypeId: null,
        CurrencyName: ''
      }));
      return;
    }

    const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(supplierId));

    if (selectedSupplier) {
      setFormData(prev => ({
        ...prev,
        supplierId: supplierId,
        supplier: selectedSupplier.Name || '',
        vatNo: selectedSupplier.VATNumber || selectedSupplier.VATNo || '',
        invType: selectedSupplier.InvoiceTypeName || selectedSupplier.InvoiceType || '',
        CurrencyTypeId: selectedSupplier.CurrencyTypeId || prev.CurrencyTypeId || null,
        CurrencyName: selectedSupplier.CurrencyName || prev.CurrencyName || ''
      }));
    }
  };

  // Recalculate when InvoiceType changes
  useEffect(() => {
    if (items.length > 0 && (formData.invType || formData.gstType)) {

      const updatedItems = items.map(item => {
        const qty = Math.max(0, parseFloat(item.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(item.rate) || 0);
        const discPercent = Math.max(0, parseFloat(item.discPercent) || 0);
        const vatRate = Math.max(0, parseFloat(item.vatPercent) || 0);
        const isIncludingTax = formData.Type === 'Including Tax';

        let taxableAmount, vatAmount, discountAmount;

        if (isIncludingTax) {
          const totalWithVat = qty * rate;
          const baseAmount = totalWithVat / (1 + vatRate / 100);
          discountAmount = (baseAmount * discPercent) / 100;
          taxableAmount = baseAmount - discountAmount;
          vatAmount = (taxableAmount * vatRate) / 100;
        } else {
          const baseAmount = qty * rate;
          discountAmount = (baseAmount * discPercent) / 100;
          taxableAmount = baseAmount - discountAmount;
          vatAmount = (taxableAmount * vatRate) / 100;
        }

        return {
          ...item,
          discAmt: Math.round(discountAmount * 100) / 100,
          taxable: Math.round(taxableAmount * 100) / 100,
          vatAmount: Math.round(vatAmount * 100) / 100,
          igst: 0,
          total: Math.round((taxableAmount + vatAmount) * 100) / 100
        };
      });

      setItems(updatedItems);
      calculateSummary(updatedItems, additionalCharges);
    }
  }, [formData.invType, formData.gstType]);

  const handleChange = (field, value) => {
    console.log('handleChange called:', field, value);
    if (field === 'supplierId') {
      handleSupplierChange(value);
    } else if (field === 'purchaseId') {
      handlePurchaseChange(value);
    } else if (field === 'vatType') {
      // Update VAT type and recalculate all items
      setFormData(prev => ({ ...prev, [field]: value }));

      // Recalculate all items with new VAT type
      const updatedItems = items.map(item => {
        const rcvdQty = parseFloat(item.rcvdQty) || 0;
        const rate = parseFloat(item.rate) || 0;
        const discPercent = parseFloat(item.discPercent) || 0;
        const vatPercent = parseFloat(item.vatPercent) || 0;

        let amount, discAmt, taxable, vatAmount, total;

        if (value === 'Including Tax') {
          // Rate includes VAT - calculate backwards
          const totalWithVat = rcvdQty * rate;
          amount = totalWithVat / (1 + vatPercent / 100);
          discAmt = (amount * discPercent) / 100;
          taxable = amount - discAmt;
          vatAmount = (taxable * vatPercent) / 100;
          total = taxable + vatAmount;
        } else {
          // Rate excludes VAT - normal calculation
          amount = rcvdQty * rate;
          discAmt = (amount * discPercent) / 100;
          taxable = amount - discAmt;
          vatAmount = (taxable * vatPercent) / 100;
          total = taxable + vatAmount;
        }

        return {
          ...item,
          amount: Math.round(amount * 100) / 100,
          discAmt: Math.round(discAmt * 100) / 100,
          taxable: Math.round(taxable * 100) / 100,
          vatAmount: Math.round(vatAmount * 100) / 100,
          igst: 0,
          total: Math.round(total * 100) / 100
        };
      });

      setItems(updatedItems);
      calculateSummary(updatedItems, additionalCharges);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Fetch single Purchase details
  const fetchSinglePurchase = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/PToPRNewOrExistingDetails/${purchaseId}/${companyId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single purchase:', error);
      return null;
    }
  };

  // Handle purchase selection - auto-fill supplier and other details
  const handlePurchaseChange = async (purchaseId) => {
    console.log('Purchase selected:', purchaseId);

    if (!purchaseId) {
      // Reset purchase-related fields
      setFormData(prev => ({
        ...prev,
        purchaseId: '',
        purchaseNo: ''
      }));
      setItems([]); // Clear items
      return;
    }

    try {
      // Fetch full purchase details using the API
      const purchaseDetails = await fetchSinglePurchase(purchaseId);

      if (purchaseDetails) {
        const purchaseData = purchaseDetails.Purchase || purchaseDetails;
        const details = purchaseDetails.PurchaseDetails || [];

        console.log('Fetched purchase data:', purchaseData);
        console.log('Fetched purchase details:', details);

        // Auto-fill supplier and other details from purchase
        // Resolve currency from purchase data
        let currencyTypeId = purchaseData.CurrencyTypeId || null;
        let currencyName = purchaseData.CurrencyName || '';

        // If CurrencyTypeId exists but CurrencyName is missing, look it up from currencies list
        if (currencyTypeId && !currencyName && Array.isArray(currencies) && currencies.length > 0) {
          const matchedCurrency = currencies.find(c => c.Id === parseInt(currencyTypeId));
          if (matchedCurrency) {
            currencyName = matchedCurrency.Currency || '';
          }
        }

        // If CurrencyName exists but CurrencyTypeId is missing, look it up from currencies list
        if (!currencyTypeId && currencyName && Array.isArray(currencies) && currencies.length > 0) {
          const matchedCurrency = currencies.find(c =>
            c.Currency?.toLowerCase() === currencyName.toLowerCase()
          );
          if (matchedCurrency) {
            currencyTypeId = matchedCurrency.Id;
            currencyName = matchedCurrency.Currency || currencyName;
          }
        }

        setFormData(prev => ({
          ...prev,
          purchaseId: purchaseId,
          purchaseNo: purchaseData.BillNumber || '',
          purchaseDate: purchaseData.BillDate ? purchaseData.BillDate.split('T')[0] : null,
          supplierId: purchaseData.MasterId || '',
          supplier: purchaseData.MasterName || '',
          vatNo: purchaseData.VATNumber || '',
          invType: purchaseData.InvoiceTypeName || '',
          vatType: purchaseData.VATCalculationType === 2 ? 'Including Tax' : 'Excluding Tax',
          CurrencyTypeId: currencyTypeId,
          CurrencyName: currencyName
        }));

        // Populate items from purchase details
        const purchaseItems = details.map(detail => ({
          id: Date.now() + Math.random(), // Unique ID
          itemId: detail.ItemId || '',
          item: detail.ItemName || '',
          hsnCode: detail.HSNCode || '',
          purchasedQty: detail.ReceivedQuantity || 0, // Store purchased quantity
          returnedQty: detail.ReturnedQty || 0, // Already returned quantity
          rcvdQty: 0, // Return quantity starts at 0
          rate: detail.Rate || 0,
          amount: 0,
          discPercent: detail.DiscountPercentage || 0,
          discAmt: 0,
          vatPercent: detail.VATRate || 0,
          taxable: 0,
          // cgst: 0,
          vatAmount: 0,
          igst: 0,
          total: 0
        }));

        setItems(purchaseItems);
        calculateSummary(purchaseItems, additionalCharges);
      }
    } catch (error) {
      console.error('Error handling purchase selection:', error);
    }
  };

  const handleAdditionalChargeChange = (field, value) => {
    // For remarks field, keep as string; for others, convert to number
    const processedValue = field === 'remarks' ? value : (parseFloat(value) || 0);
    setAdditionalCharges(prev => {
      const updated = { ...prev, [field]: processedValue };
      calculateSummary(items, updated);
      return updated;
    });
  };

  const handleAddItem = () => {
    // Validation: Check if the last row has data before adding a new row
    if (items.length > 0) {
      const lastItem = items[items.length - 1];

      // Check if the last item has an item selected and return quantity > 0
      if (!lastItem.itemId || lastItem.itemId === '') {
        setValidationError('Please select an item in the current row before adding a new one.');
        return;
      }

      if (!lastItem.rcvdQty || parseFloat(lastItem.rcvdQty) <= 0) {
        setValidationError('Please enter Return Qty in the current row before adding a new one.');
        return;
      }
    }

    // Clear validation error if validation passes
    setValidationError('');

    setItems(prev => [...prev, {
      id: Date.now(),
      itemId: '',
      item: '',
      hsnCode: '',
      purchasedQty: 0,
      rcvdQty: 0,
      rate: 0,
      amount: 0,
      discPercent: 0,
      discAmt: 0,
      vatPercent: 0,
      taxable: 0,
      //cgst: 0,
      vatAmount: 0,
      igst: 0,
      total: 0
    }]);
  };

  const handleDeleteItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    calculateSummary(updatedItems, additionalCharges);
  };

  const handleItemChange = (id, field, value) => {
    const updatedItems = items.map(item => {
      if (item.id !== id) return item;

      const updated = { ...item, [field]: value };

      // If item is selected from dropdown, populate item details
      if (field === 'itemId' && value) {
        const selectedItem = itemsList.find(i => i.Id === parseInt(value));
        if (selectedItem) {
          console.log('Selected item data:', selectedItem);
          updated.item = selectedItem.Name || '';
          updated.hsnCode = selectedItem.HSNCode || selectedItem.HSN || '';
          updated.vatPercent = selectedItem.VATRate || selectedItem.GST || 0;
          // Try multiple rate fields
          updated.rate = selectedItem.PurchaseRate || selectedItem.Rate || selectedItem.SaleRate || selectedItem.MRP || 0;
          console.log('Updated rate:', updated.rate);
        }
      }

      // Validate return quantity doesn't exceed available quantity (purchased - already returned)
      if (field === 'rcvdQty') {
        const returnQty = parseFloat(value) || 0;
        const purchasedQty = parseFloat(item.purchasedQty) || 0;
        const alreadyReturned = parseFloat(item.returnedQty) || 0;
        const availableQty = purchasedQty - alreadyReturned;

        // If purchased quantity exists and return quantity exceeds available, cap it
        if (purchasedQty > 0 && returnQty > availableQty) {
          updated.rcvdQty = availableQty;
        }
      }

      // Recalculate when quantity, rate, discount, or VAT changes
      if (['rcvdQty', 'rate', 'discPercent', 'discAmt', 'vatPercent', 'itemId'].includes(field)) {
        const qty = Math.max(0, parseFloat(updated.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(updated.rate) || 0);
        const vatRate = Math.max(0, parseFloat(updated.vatPercent) || 0);
        const isIncludingTax = formData.vatType === 'Including Tax';

        let taxableAmount, vatAmount, amount, discountAmount, discountPercent;
        let baseAmount;

        if (isIncludingTax) {
          // Rate includes VAT - reverse calculate
          const totalWithvat = qty * rate;
          baseAmount = totalWithvat / (1 + vatRate / 100);
        } else {
          // Rate excludes VAT - forward calculate
          baseAmount = qty * rate;
        }

        // If discAmt is being changed directly, calculate discPercent from it
        if (field === 'discAmt') {
          discountAmount = Math.max(0, parseFloat(updated.discAmt) || 0);
          // Calculate discount percentage from amount
          discountPercent = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
          updated.discPercent = Math.round(discountPercent * 100) / 100;
        } else {
          // Calculate discAmt from discPercent
          discountPercent = Math.max(0, parseFloat(updated.discPercent) || 0);
          discountAmount = (baseAmount * discountPercent) / 100;
          updated.discAmt = Math.round(discountAmount * 100) / 100;
        }

        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = taxableAmount + vatAmount;

        // Round values
        updated.amount = Math.round((qty * rate) * 100) / 100;
        updated.taxable = Math.round(taxableAmount * 100) / 100;
        updated.vatAmount = Math.round(vatAmount * 100) / 100;
        updated.igst = 0;
        updated.total = Math.round(amount * 100) / 100;
      }

      return updated;
    });

    setItems(updatedItems);
    calculateSummary(updatedItems, additionalCharges);
  };

  const calculateSummary = (itemsList, charges) => {

    // Find the maximum VAT rate from items
    const maxItemVATRate = itemsList.reduce((max, item) => {
      const vatRate = Math.max(0, parseFloat(item.vatPercent) || 0);
      return Math.max(max, vatRate);
    }, 0);

    // Default freight and packing VAT rate is 5%, but use max item VAT if it's higher
    const defaultFreightPackingVATRate = 5;
    const effectiveVATRate = Math.max(maxItemVATRate, defaultFreightPackingVATRate);

    const itemTotals = itemsList.reduce((acc, item) => {
      const qty = Math.max(0, parseFloat(item.rcvdQty) || 0);
      const rate = Math.max(0, parseFloat(item.rate) || 0);
      const discPercent = Math.max(0, parseFloat(item.discPercent) || 0);
      const vatRate = Math.max(0, parseFloat(item.vatPercent) || 0);
      const isIncludingTax = formData.vatType === 'Including Tax';

      let taxableAmount, vatAmount, discountAmount;

      if (isIncludingTax) {
        const totalWithVat = qty * rate;
        const baseAmount = totalWithVat / (1 + vatRate / 100);
        discountAmount = (baseAmount * discPercent) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
      } else {
        const baseAmount = qty * rate;
        discountAmount = (baseAmount * discPercent) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
      }

      return {
        subtotal: acc.subtotal + (qty * rate),
        discount: acc.discount + discountAmount,
        taxable: acc.taxable + taxableAmount,
        vatAmount: acc.vatAmount + vatAmount
      };
    }, { subtotal: 0, discount: 0, taxable: 0, vatAmount: 0 });

    const freight = parseFloat(charges.freight) || 0;
    const packing = parseFloat(charges.packing) || 0;
    const tcs = parseFloat(charges.tcsAmount) || 0;
    const paid = parseFloat(charges.paidAmount) || 0;

    // Calculate freight and packing VAT
    const freightVAT = (freight * effectiveVATRate) / 100;
    const packingVAT = (packing * effectiveVATRate) / 100;
    const totalFreightPackingVAT = freightVAT + packingVAT;

    const grandTotal = itemTotals.taxable + itemTotals.vatAmount + freight + packing + totalFreightPackingVAT + tcs;
    const total = Math.round(grandTotal);
    const roundOff = total - grandTotal;

    setSummary({
      subtotal: Math.round(itemTotals.subtotal * 100) / 100,
      discount: Math.round(itemTotals.discount * 100) / 100,
      taxable: Math.round(itemTotals.taxable * 100) / 100,
      vatamount: Math.round(itemTotals.vatAmount * 100) / 100,
      igst: 0,
      freightVAT: Math.round(freightVAT * 100) / 100,
      packingVAT: Math.round(packingVAT * 100) / 100,
      effectiveVATRate: effectiveVATRate,
      tcs: tcs,
      roundOff: Math.round(roundOff * 100) / 100,
      total: total,
      paid: paid
    });
  };

  const handleSubmit = async () => {
    // Clear previous validation errors
    setValidationError('');

    // Validation: Check if there are any items
    if (items.length === 0) {
      setValidationError('Please add at least one item to the return.');
      return;
    }

    // Validation: Check if at least one item has Return Qty > 0
    const itemsWithReturnQty = items.filter(item => (parseFloat(item.rcvdQty) || 0) > 0);

    if (itemsWithReturnQty.length === 0) {
      setValidationError('Please enter Return Qty for at least one item.');
      return;
    }

    // Validation: Check if items with Return Qty have Item selected
    const invalidItems = [];
    itemsWithReturnQty.forEach((item, index) => {
      if (!item.itemId || item.itemId === '') {
        invalidItems.push(index + 1);
      }
    });

    if (invalidItems.length > 0) {
      setValidationError(`Please select an item for row(s): ${invalidItems.join(', ')}`);
      return;
    }

    // Additional validation
    const validationErrors = [];

    if (!formData.billNo?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!formData.billDate) {
      validationErrors.push('Return Date is required');
    }

    if (validationErrors.length > 0) {
      setValidationError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const vatCalculationType = formData.vatType === 'Including Tax' ? 2 : 1;

      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseNo: formData.purchaseNo || '',
          PurchaseDate: formData.purchaseDate || null,
          BillNumber: formData.billNo,
          BillDate: formData.billDate,
          MasterId: parseInt(formData.supplierId),
          MasterName: formData.supplier,
          InvoiceTypeId: 0,
          VATNumber: formData.vatNo || '',
          VATCalculationType: vatCalculationType,
          VATCalculationTypeName: formData.vatType || 'Excluding Tax',
          CurrencyTypeId: parseInt(formData.CurrencyTypeId) || 0,
          CurrencyName: formData.CurrencyName || '',
          Amount: parseFloat(summary.subtotal) || 0,
          DiscountAmount: parseFloat(summary.discount) || 0,
          TaxableAmount: parseFloat(summary.taxable) || 0,
          VATAmount: parseFloat(summary.vatamount) || 0,
          IGSTAmount: 0,
          FreightAmount: parseFloat(additionalCharges.freight) || 0,
          FreightVATRate: parseFloat(summary.effectiveVATRate) || 0,
          FreightVATAmount: parseFloat(summary.freightVAT) || 0,
          PackingAmount: parseFloat(additionalCharges.packing) || 0,
          PackingVATRate: parseFloat(summary.effectiveVATRate) || 0,
          PackingVATAmount: parseFloat(summary.packingVAT) || 0,
          TCSAmount: parseFloat(additionalCharges.tcsAmount) || 0,
          RoundOff: parseFloat(summary.roundOff) || 0,
          TotalAmount: parseFloat(summary.total) || 0,
          PaidAmount: parseFloat(additionalCharges.paidAmount) || 0,
          BalanceAmount: parseFloat(summary.total - additionalCharges.paidAmount) || 0,
          Remarks: additionalCharges.remarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(item => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0,
          ItemId: parseInt(item.itemId),
          ItemName: item.item || '',
          BatchNumber: item.batchNumber || '',
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseQuantity: parseFloat(item.purchasedQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          ReturnStockQuantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: parseFloat((item.rcvdQty || 0) * (item.rate || 0)) || 0,
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '',
          VATRate: parseFloat(item.vatPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          //CGSTAmount: parseFloat(item.cgst) || 0,
          VATAmount: parseFloat(item.vatAmount) || 0,
          IGSTAmount: 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      console.log('Data to send:', dataToSend);

      await postData('PurchaseReturnApi', '', dataToSend, token);

      setSuccess('Purchase return created successfully!');
      setCreateModalOpen(false);
      fetchPurchaseReturns();
      onClose();
    } catch (error) {
      console.error('Error creating purchase return:', error);
      setError('Failed to create purchase return.');
    }

    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '95vh',
          height: '95vh'
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        p: 0.75,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#333' }}>
          ⭐ Create Purchase Return
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ p: 0.2 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        p: { xs: 1, md: 0.5 },
        bgcolor: '#fafafa',
        overflow: 'auto'
      }}>
        {/* Validation Error Message */}
        {validationError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => setValidationError('')}>
              {validationError}
            </Alert>
          </Box>
        )}

        {/* Purchase Information Section */}
        <Box sx={{ mb: { xs: 1, md: 0.5 } }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, mb: { xs: 0.5, md: 0.3 } }}>
            Purchase Return Information
          </Typography>
          <Grid container spacing={{ xs: 0.75, md: 0.5 }}>
            <Grid item xs={12} sm={6} md={1.3}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Bill No *
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.billNo}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: '#f5f5f5',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.3}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Bill Date *
              </Typography>
              <TextField
                fullWidth
                type="date"
                size="small"
                value={formData.billDate}
                onChange={(e) => handleChange('billDate', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.2}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Supplier *
              </Typography>
              <TextField
                fullWidth
                select
                size="small"
                value={formData.supplierId}
                onChange={(e) => handleChange('supplierId', e.target.value)}
                SelectProps={{
                  native: false,
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    height: '28px'
                  }
                }}
              >
                <MenuItem value="">Select Supplier</MenuItem>
                {suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                    <MenuItem key={supplier.MasterId || supplier.Id} value={supplier.MasterId || supplier.Id}>
                      {supplier.Name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>No suppliers available</MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={1.8}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                VAT No
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.vatNo}
                onChange={(e) => handleChange('gstNo', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={1.3}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Inv Type
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.invType}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: '#f5f5f5',
                    height: '28px'
                  }
                }}
              />
            </Grid> */}

            <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Currency
              </Typography>
              <Autocomplete
                options={Array.isArray(currencies) ? currencies : []}
                getOptionLabel={(option) => option.Currency || ''}
                isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                value={Array.isArray(currencies) && formData.CurrencyTypeId ? currencies.find(c => c.Id === parseInt(formData.CurrencyTypeId)) || null : null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setFormData(prev => ({
                      ...prev,
                      CurrencyTypeId: newValue.Id,
                      CurrencyName: newValue.Currency || ''
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      CurrencyTypeId: null,
                      CurrencyName: ''
                    }));
                  }
                }}
                disabled={currencyLoading}
                loading={currencyLoading}
                ListboxProps={{
                  style: { fontSize: '0.7rem', maxHeight: '200px' }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.7rem',
                        bgcolor: 'white',
                        height: '28px'
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: '0.7rem' },
                      endAdornment: (
                        <>
                          {currencyLoading ? <CircularProgress color="inherit" size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                sx={{
                  '& .MuiInputBase-root': { minHeight: '28px', py: '0 !important' },
                  '& .MuiInputBase-input': { py: '0.4 !important', fontSize: '0.7rem' },
                  '& .MuiAutocomplete-input': { py: '3.5px !important' },
                  '& .MuiOutlinedInput-root': { minHeight: '28px', padding: '0 9px !important' },
                  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.3}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                VAT Type *
              </Typography>
              <TextField
                fullWidth
                select
                size="small"
                value={formData.vatType}
                onChange={(e) => handleChange('vatType', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    height: '28px'
                  }
                }}
              >
                <MenuItem value="Excluding Tax">Excluding Tax</MenuItem>
                <MenuItem value="Including Tax">Including Tax</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Purchase No (Optional)
              </Typography>
              <TextField
                fullWidth
                select
                size="small"
                value={formData.purchaseId}
                onChange={(e) => handleChange('purchaseId', e.target.value)}
                SelectProps={{
                  native: false,
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    height: '28px'
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {purchases.filter(p => p.IsConvertToGoodReceipt && p.IsVerified).length > 0 ? (
                  purchases.filter(p => p.IsConvertToGoodReceipt && p.IsVerified).map((purchase) => (
                    <MenuItem key={purchase.Id} value={purchase.Id}>
                      {purchase.BillNumber} - {purchase.MasterName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>No purchases available</MenuItem>
                )}
              </TextField>
            </Grid>

          </Grid>
        </Box>

        {/* Return Items Section */}
        <Box sx={{ mb: { xs: 1, md: 0.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.5, md: 0.3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>
                Return Items {items.length > 0 && `(${items.length})`}
              </Typography>
              {items.length > 7 && (
                <Button
                  size="small"
                  onClick={() => setViewAllItemsModalOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.6rem',
                    color: '#1976d2',
                    textDecoration: 'underline',
                    minWidth: 'auto',
                    p: 0,
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                      color: '#1565c0'
                    }
                  }}
                >
                  View All ({items.length} items)
                </Button>
              )}
            </Box>
            {/* <Button
              variant="contained"
              size="small"
              startIcon={<Add fontSize="small" />}
              onClick={handleAddItem}
              sx={{
                textTransform: 'none',
                fontSize: '0.6rem',
                bgcolor: '#1976d2',
                py: 0.3,
                px: 0.75,
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Add Item
            </Button> */}
          </Box>
          <TableContainer component={Paper} sx={{
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            maxHeight: '300px',
            overflow: 'auto',
            borderRadius: '6px'
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', minWidth: 180, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Item</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>HSN</TableCell>
                  {formData.purchaseId && (
                    <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Purchased Qty</TableCell>
                  )}
                  {formData.purchaseId && (
                    <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#dc2626', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Returned Qty</TableCell>
                  )}
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Return Qty *</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Rate</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Disc%</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Disc Amt</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>VAT%</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Taxable</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>
                    VAT Total
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Total</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', border: '1px solid #fde68a', width: '40px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 3, color: '#999', fontSize: '0.75rem' }}>
                      Select Item
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                      <TableCell sx={{ px: 0.4, py: 0.3, fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>{index + 1}</TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, border: '1px solid #e5e7eb' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b21a8', px: 0.5 }}>
                          {item.item || itemsList.find(itm => itm.Id === parseInt(item.itemId))?.Name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.6rem', color: '#666', border: '1px solid #e5e7eb' }}>
                        {item.hsnCode || '-'}
                      </TableCell>
                      {formData.purchaseId && (
                        <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                          {item.purchasedQty || 0}
                        </TableCell>
                      )}
                      {formData.purchaseId && (
                        <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#dc2626', border: '1px solid #e5e7eb' }}>
                          {item.returnedQty || 0}
                        </TableCell>
                      )}
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                        {formData.purchaseId && (parseFloat(item.purchasedQty) || 0) > 0 && (parseFloat(item.returnedQty) || 0) >= (parseFloat(item.purchasedQty) || 0) ? (
                          <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 600 }}>Fully Returned</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={item.rcvdQty}
                            onChange={(e) => handleItemChange(item.id, 'rcvdQty', e.target.value)}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ min: 0, max: (parseFloat(item.purchasedQty) || 999999) - (parseFloat(item.returnedQty) || 0), step: 1, style: { textAlign: 'center' } }}
                            sx={{ width: '65px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                          />
                        )}
                      </TableCell>
                      {formData.purchaseId && (parseFloat(item.purchasedQty) || 0) > 0 && (parseFloat(item.returnedQty) || 0) >= (parseFloat(item.purchasedQty) || 0) ? (
                        <>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{item.rate}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{formatCurrency(item.purchasedQty * item.rate)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{item.discPercent}%</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{formatCurrency(item.discAmt || 0)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{item.vatPercent}%</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{formatCurrency(item.taxable || 0)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{formatCurrency(item.vatAmount || 0)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{formatCurrency(item.total || 0)}</TableCell>
                        </>
                      ) : (
                        <>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '75px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#666', border: '1px solid #e5e7eb' }}>
                        {formatCurrency(item.amount || 0)}
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.discPercent}
                          onChange={(e) => handleItemChange(item.id, 'discPercent', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '55px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.discAmt}
                          onChange={(e) => handleItemChange(item.id, 'discAmt', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '65px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb' }}>
                        {item.vatPercent}%
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>
                        {formatCurrency(item.taxable || 0)}
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>
                        {formatCurrency(item.vatAmount || 0)}
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', fontWeight: 600, border: '1px solid #e5e7eb' }}>
                        {formatCurrency(item.total || 0)}
                      </TableCell>
                        </>
                      )}
                      <TableCell sx={{ px: 0.4, py: 0.3, border: '1px solid #e5e7eb' }}>
                        {!(formData.purchaseId && (parseFloat(item.purchasedQty) || 0) > 0 && (parseFloat(item.returnedQty) || 0) >= (parseFloat(item.purchasedQty) || 0)) && (
                          <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error" sx={{ p: 0.2 }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {/* Totals Row */}
                {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                    <TableCell colSpan={3} sx={{ px: 0.4, py: 0.4, fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb', textAlign: 'right' }}>
                      Total:
                    </TableCell>
                    {formData.purchaseId && (
                      <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                        {items.reduce((sum, item) => sum + (parseFloat(item.purchasedQty) || 0), 0)}
                      </TableCell>
                    )}
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.rcvdQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.taxable) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.vatAmount) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Return Summary Section */}
        <Box sx={{ mb: { xs: 1, md: 0.5 } }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333', mb: { xs: 0.5, md: 0.3 } }}>
            Return Summary
          </Typography>
          <Grid container spacing={{ xs: 1, md: 0.5 }}>
            {/* Additional Charges */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#666', mb: { xs: 0.5, md: 0.3 } }}>
                Additional Charges
              </Typography>
              <Box sx={{ bgcolor: '#fafafa', p: { xs: 1, md: 0.75 }, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={{ xs: 0.75, md: 0.5 }}>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Freight Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      VAT Rate: {summary.effectiveVATRate}%
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.freight}
                      onChange={(e) => handleAdditionalChargeChange('freight', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Packing Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      VAT Rate: {summary.effectiveVATRate}%
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.packing}
                      onChange={(e) => handleAdditionalChargeChange('packing', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  {/* <Grid item xs={6}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      TCS Amount
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.tcsAmount}
                      onChange={(e) => handleAdditionalChargeChange('tcsAmount', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid> */}
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Paid Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      -
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.paidAmount}
                      onChange={(e) => handleAdditionalChargeChange('paidAmount', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Return Remarks
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type='text'
                      multiline
                      rows={1.5}
                      value={additionalCharges.remarks}
                      onChange={(e) => handleAdditionalChargeChange('remarks', e.target.value)}
                      placeholder="Enter return remarks..."
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white' } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Return Calculation */}
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#666', mb: { xs: 0.5, md: 0.3 } }}>
                Return Calculation
              </Typography>
              <Box sx={{ bgcolor: '#fafafa', p: { xs: 0.75, md: 0.5 }, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={{ xs: 0.5, md: 0.3 }}>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Subtotal</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{formatCurrency(summary.subtotal)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Discount</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>{formatCurrency(summary.discount)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Taxable</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{formatCurrency(summary.taxable)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>
                        VAT Total
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669' }}>
                        {formatCurrency(summary.vatamount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Freight</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{formatCurrency(additionalCharges.freight || 0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>
                        Freight VAT
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{formatCurrency(summary.freightVAT || 0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Packing</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{formatCurrency(additionalCharges.packing || 0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>
                        Packing VAT
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{formatCurrency(summary.packingVAT || 0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Round Off</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>
                        {summary.roundOff > 0 ? '+ ' : summary.roundOff < 0 ? '- ' : ''}{formatCurrency(Math.abs(summary.roundOff))}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={0.5} sx={{ mt: 0.2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: '#fff3e0', p: 0.5, borderRadius: 1, border: '1.5px solid #ffb74d', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#e65100', mb: 0.1, fontWeight: 600 }}>Return Total Amount</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#e65100' }}>{formatCurrency(summary.total)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: '#e3f2fd', p: 0.5, borderRadius: 1, border: '1.5px solid #64b5f6', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#1565c0', mb: 0.1, fontWeight: 600 }}>Balance Amount</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1565c0' }}>
                        {formatCurrency(summary.total - summary.paid)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 0.75,
        borderTop: '1px solid #e0e0e0',
        bgcolor: '#f5f5f5',
        gap: 0.5
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '0.7rem',
            py: 0.4,
            px: 1.25,
            borderColor: '#999',
            color: '#666',
            '&:hover': {
              borderColor: '#666',
              bgcolor: '#f5f5f5'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
            fontSize: '0.7rem',
            py: 0.4,
            px: 1.25,
            bgcolor: '#333',
            '&:hover': {
              bgcolor: '#555'
            }
          }}
        >
          Save Changes
        </Button>
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog
        open={viewAllItemsModalOpen}
        onClose={() => setViewAllItemsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>
            All Return Items ({items.length})
          </Typography>
          <IconButton onClick={() => setViewAllItemsModalOpen(false)} size="small" sx={{ p: 0.5 }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, bgcolor: '#fafafa' }}>
          <TableContainer component={Paper} sx={{ border: '1px solid #ddd', boxShadow: 'none', maxHeight: '500px', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#ffe4cc', height: '32px' }}>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', border: '1px solid #ffd4a3', height: '32px' }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', minWidth: 200, border: '1px solid #ffd4a3', height: '32px' }}>Item</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>HSN</TableCell>
                  {formData.purchaseId && (
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Purchased Qty</TableCell>
                  )}
                  {formData.purchaseId && (
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#dc2626', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Returned Qty</TableCell>
                  )}
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Return Qty</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Rate</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Disc%</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Disc Amt</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>VAT%</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Taxable</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>
                    VAT Total
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Total</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', border: '1px solid #ffd4a3', height: '32px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, height: '40px' }}>
                    <TableCell sx={{ p: 0.1, py: 0.3, fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>{index + 1}</TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb', height: '40px' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b21a8', px: 0.5 }}>
                        {item.item || itemsList.find(itm => itm.Id === parseInt(item.itemId))?.Name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.75rem', color: '#666', border: '1px solid #e5e7eb', height: '40px' }}>
                      {item.hsnCode || '-'}
                    </TableCell>
                    {formData.purchaseId && (
                      <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#1976d2', border: '1px solid #e5e7eb', height: '40px' }}>
                        {item.purchasedQty || 0}
                      </TableCell>
                    )}
                    {formData.purchaseId && (
                      <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>
                        {item.returnedQty || 0}
                      </TableCell>
                    )}
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', border: '1px solid #e5e7eb', height: '40px' }}>
                      {formData.purchaseId && (parseFloat(item.purchasedQty) || 0) > 0 && (parseFloat(item.returnedQty) || 0) >= (parseFloat(item.purchasedQty) || 0) ? (
                        <Typography sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>Fully Returned</Typography>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={item.rcvdQty}
                          onChange={(e) => handleItemChange(item.id, 'rcvdQty', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, max: (parseFloat(item.purchasedQty) || 999999) - (parseFloat(item.returnedQty) || 0), step: 1, style: { textAlign: 'center' } }}
                          sx={{ width: '80px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                        />
                      )}
                    </TableCell>
                    {formData.purchaseId && (parseFloat(item.purchasedQty) || 0) > 0 && (parseFloat(item.returnedQty) || 0) >= (parseFloat(item.purchasedQty) || 0) ? (
                      <>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{item.rate}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{formatCurrency(item.purchasedQty * item.rate)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{item.discPercent}%</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{formatCurrency(item.discAmt || 0)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{item.vatPercent}%</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{formatCurrency(item.taxable || 0)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{formatCurrency(item.vatAmount || 0)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{formatCurrency(item.total || 0)}</TableCell>
                      </>
                    ) : (
                      <>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '90px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#666', border: '1px solid #e5e7eb', height: '40px' }}>
                      {formatCurrency(item.amount || 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.discPercent}
                        onChange={(e) => handleItemChange(item.id, 'discPercent', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '70px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.discAmt}
                        onChange={(e) => handleItemChange(item.id, 'discAmt', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '80px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb', height: '40px' }}>
                      {item.vatPercent}%
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      {formatCurrency(item.taxable || 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      {formatCurrency(item.vatAmount || 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, border: '1px solid #e5e7eb', height: '40px' }}>
                      {formatCurrency(item.total || 0)}
                    </TableCell>
                      </>
                    )}

                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb', height: '40px' }}>
                      {!(formData.purchaseId && (parseFloat(item.purchasedQty) || 0) > 0 && (parseFloat(item.returnedQty) || 0) >= (parseFloat(item.purchasedQty) || 0)) && (
                        <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error" sx={{ p: 0.1 }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                    <TableCell colSpan={3} sx={{ p: 0.1, py: 0.3, fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb', textAlign: 'right' }}>
                      Total:
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.purchasedQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.rcvdQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.taxable) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.vatAmount) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0))}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
          <Button
            variant="outlined"
            onClick={() => setViewAllItemsModalOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              borderColor: '#999',
              color: '#666',
              '&:hover': {
                borderColor: '#666',
                bgcolor: '#f5f5f5'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PurchaseReturnModal;
